import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { WalletType } from "../../../types/walletType";

export interface WalletTypeFormData {
  name: string;
  description: string;
  isFallback: boolean;
}

interface Props {
  onClose: () => void;
  onSave: (data: WalletTypeFormData, id: string | undefined) => void;
  walletType?: WalletType;
}

export default function EditWalletTypeDialog(props: Props) {
  const { t } = useTranslation();

  const [error, setError] = useState("");
  const [formData, setFormData] = useState<WalletTypeFormData>({
    name: "",
    description: "",
    isFallback: false,
  });

  const isEdit = !!props.walletType;
  const isCurrentFallback = props.walletType?.isFallback ?? false;
  const dialogTitle = isEdit
    ? t("walletTypes.dialog.editTitle")
    : t("walletTypes.dialog.createTitle");

  useEffect(() => {
    setFormData({
      name: props.walletType?.name ?? "",
      description: props.walletType?.description ?? "",
      isFallback: props.walletType?.isFallback ?? false,
    });
    setError("");
  }, [props.walletType]);

  const validateInput = (): boolean => {
    if (formData.name.trim().length === 0) {
      setError(t("dialogModals.fieldNotFilled"));
      return false;
    }
    setError("");
    return true;
  };

  const handleSave = () => {
    if (!validateInput()) {
      return;
    }
    props.onSave(formData, props.walletType?.id);
    props.onClose();
  };

  return (
    <Dialog open={true} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t("walletTypes.dialog.nameLabel")}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          onBlur={validateInput}
          error={!!error && formData.name.trim().length === 0}
          sx={{ mt: 1 }}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t("walletTypes.dialog.descriptionLabel")}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          sx={{ mt: 3 }}
        />

        {!!error && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}

        {isEdit && (
          <Stack sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isFallback}
                  disabled={isCurrentFallback}
                  onChange={(e) =>
                    setFormData({ ...formData, isFallback: e.target.checked })
                  }
                />
              }
              label={t("walletTypes.dialog.fallbackLabel")}
            />
            {isCurrentFallback && (
              <Typography variant="caption" color="text.secondary">
                {t("walletTypes.dialog.fallbackDisabledHint")}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <GeneralButton appVariant="secondary" onClick={props.onClose}>
          {t("buttons.cancel")}
        </GeneralButton>
        <GeneralButton appVariant="primary" onClick={handleSave}>
          {t("buttons.save")}
        </GeneralButton>
      </DialogActions>
    </Dialog>
  );
}
